import { CookiesProvider } from "react-cookie";
import { buildUrlsOnPage } from "./src/utils/environment-urls";
import { PrismicPreviewProvider } from "gatsby-plugin-prismic-previews";
import { prismicPreviewConfig } from "./src/templates";
import { GatsbyBrowser } from "gatsby";
import React from "react";
import { PrismicToolbar } from "@prismicio/react";
import { origins } from "./src/utils/environment-urls";

import { baseDomain, prismicRepo } from "./src/utils/constants";
import { UserValidationContextStore } from "./src/utils/contexts/user-validation-context";
import { ApolloProvider } from "@apollo/client";
import apolloClient from "./src/utils/apollo";
import "./src/styles/global.css";
import XomErrorBoundary from "./xom-error-boundary";
import { CoralogixProvider } from "./src/utils/contexts/coralogix-context";
import * as Sentry from "@sentry/react";
import { isCrawler } from "./src/utils/isCrawler";
import {
  StatsigWrapper,
  ensureStatsigClient,
} from "./src/utils/statsig/statsig-provider";
import { installHardNavInterceptor } from "./src/utils/statsig/hard-nav-interceptor";

// Cache crawler check - called once at module load (MKTM-839)
const isBot = isCrawler();

// Build Sentry integrations - skip replayIntegration for crawlers
// to reduce JS render time measured by Botify/Google
const sentryIntegrations = [
  Sentry.browserTracingIntegration(),
  // Only add replay for real users, not crawlers
  ...(!isBot
    ? [
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: true,
          networkDetailAllowUrls: [origins.api, origins.www],
        }),
      ]
    : []),
];

// Initialize Sentry with reduced sample rate (MKTM-839)
// Note: replay sample rates only matter for non-crawlers since
// replayIntegration isn't loaded for crawlers
Sentry.init({
  dsn: "https://3acb4094302c9e9486229ac853fd01b6@sentry.mgmt.global.xmtr.io/65",
  environment: baseDomain.includes("stage") ? "stage" : "prod",
  integrations: sentryIntegrations,
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Kick Statsig init at bundle-eval time rather than when <StatsigWrapper>
// mounts, so the config network request fires ~500ms earlier in the load chain.
// StatsigWrapper reuses this exact singleton via the SDK's instance registry.
// No-op for crawlers / key-less envs.
ensureStatsigClient();

// Start mock service worker
// https://mswjs.io/docs/getting-started/integrate/browser
if (process.env.NODE_ENV === "development") {
  const { worker } = require("./mocks/browser");
  worker.start();
}

// Force in-app navigation to be a full HTTP request so it re-enters
// the storefront-traffic-bridge edge router instead of Gatsby's client-side
// router (which would serve a stale marketing-frontend page, or its 404, for a
// path already migrated to MWF). Logic + rationale live in hard-nav-interceptor.
const onClientEntry: GatsbyBrowser["onClientEntry"] = () => {
  installHardNavInterceptor();
};

// update meta og:url and canonical link tags with relative URLs
const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = ({ location }) => {
  // if path is localized to default language, use non-localized path
  let pathName = `${location.pathname}`;
  let defaultLangPath = "/en-US";
  if (pathName.startsWith(defaultLangPath)) {
    pathName = pathName.substring(defaultLangPath.length);
  }

  let url = `${location.origin}${pathName}${location.hash}`;

  const ogUrl = document.createElement("meta");
  ogUrl.setAttribute("property", "og:url");
  ogUrl.setAttribute("content", url);

  const canonicalLink = document.createElement("link");
  canonicalLink.setAttribute("rel", "canonical");
  canonicalLink.setAttribute("href", url);

  const header = document.querySelector("head");
  header?.appendChild(ogUrl);
  header?.appendChild(canonicalLink);

  buildUrlsOnPage();
};

const RootWrapper = ({ children }: { children: React.ReactNode }) => {
  const isPreviewPage =
    location.pathname.includes("/preview") ||
    document.referrer.includes("/preview");

  return (
    <PrismicPreviewProvider repositoryConfigs={[prismicPreviewConfig]}>
      <ApolloProvider client={apolloClient}>
        <CookiesProvider>
          <UserValidationContextStore>
            <CoralogixProvider>
              <XomErrorBoundary>
                <StatsigWrapper>{children}</StatsigWrapper>
              </XomErrorBoundary>
            </CoralogixProvider>
          </UserValidationContextStore>
        </CookiesProvider>
      </ApolloProvider>
      {isPreviewPage && <PrismicToolbar repositoryName={prismicRepo} />}
    </PrismicPreviewProvider>
  );
};

const wrapRootElement: GatsbyBrowser["wrapRootElement"] = ({ element }) => {
  return <RootWrapper>{element}</RootWrapper>;
};

const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({
  element,
  props,
}) => {
  typeof window !== "undefined" &&
    window.analytics &&
    window.analytics.page("Marketing", {
      template: props.pageContext.type,
      prismicId: props.pageContext.prismicId,
    });
  return element;
};

export { onClientEntry, onRouteUpdate, wrapRootElement, wrapPageElement };

export const onInitialClientRender: GatsbyBrowser["onInitialClientRender"] =
  () => {
    window.addEventListener("segmentLoaded", function () {
      // Adjust drift bot location to be above privacy banner if it is present
      window.analytics &&
        window.analytics.ready &&
        window.analytics.ready(function () {
          window?.drift?.on("ready", function () {
            const privacyBanner = document.getElementById("privacy-banner");
            const botController = document.getElementById(
              "drift-frame-controller"
            );
            const botChat = document.getElementById("drift-frame-chat");

            if (privacyBanner && botController && botChat) {
              botController.style.bottom =
                parseInt(botController.style.bottom) +
                privacyBanner.offsetHeight +
                "px";
              botChat.style.bottom =
                parseInt(botChat.style.bottom) +
                privacyBanner.offsetHeight +
                "px";
              botChat.style.zIndex = "9000 !important";
            }
          });
        });
    });
  };

export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = ({
  routerProps: { location },
  getSavedScrollPosition,
}) => {
  const currentPosition = getSavedScrollPosition(location);
  if (window.location.hash) {
    const target = document.querySelector(window.location.hash);
    if (target) {
      setTimeout(function () {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
    } else {
      setTimeout(() => {
        window.scrollTo(...(currentPosition || [0, 0]));
      }, 0);
    }
  } else {
    setTimeout(() => {
      window.scrollTo(...(currentPosition || [0, 0]));
    }, 0);
  }

  return false;
};
